import React, { useEffect, useMemo, useRef, useState } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import {
  CaretDownIcon,
  CheckCircleIcon,
  CircleIcon,
  ClockIcon,
  FileMagnifyingGlassIcon,
  ShieldIcon,
} from "@phosphor-icons/react";
import { MotiView } from "moti";
import { useIsFocused, useNavigation } from "@react-navigation/native";
import type { NavigationProp } from "@react-navigation/native";
import AdminLayout from "../../layouts/AdminLayout.web";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CourseInfoCard from "../../components/adminDashboard/CourseInfoCard/CourseInfoCard";
import { UserRoleUI } from "@axio/auth-guard";
import { useToast } from "../../contexts/Toast/ToastProvider";
import { useAuth } from "../../hooks/useAuth";
import useFileUpload, { type ProcessedFile } from "../../hooks/useFileUpload";
import Sentry from "../../utils/sentry";
import {
  ExternalCourse,
  ModernizeCourseGenerationStatus,
  ModernizeCourseTargetAudience,
  ModernizedAuditStatus,
  ModernizedDeliveryMode,
  RubricParseStatus,
  StoragePathType,
  type ActiveRubricSetQuery,
  type RubricSetQuery,
  type RubricSetParseStatusQuery,
  useActiveRubricSetQuery,
  useCreateRubricSetFromFileMutation,
  useGetModernizeCourseJobStatusQuery,
  useGetActiveModernizeCourseJobQuery,
  useListExternalCoursesQuery,
  useRubricSetQuery,
  useRubricSetsBySchoolQuery,
  useRubricSetParseStatusQuery,
  useSetActiveRubricSetMutation,
  useUpdateRubricSetMutation,
  useCancelModernizeExternalCourseMutation,
  useModernizeExternalCourseMutation,
} from "@axio/graphql/generated/graphql";

import type { RootStackParamList } from "../../navigation/navigation.types";
import RubricConfigSlideIn from "../../components/adminDashboard/CourseModernizer/RubricConfigSlideIn/RubricConfigSlideIn";
import CourseModernizerCoursesTable from "../../components/adminDashboard/CourseModernizer/CourseModernizerCoursesTable";
import type { CourseModernizerCourseRow } from "../../components/adminDashboard/CourseModernizer/CourseModernizerCoursesTable";
import CourseContextEvaluationModal, {
  DEFAULT_COURSE_CONTEXT,
  type CourseContextState,
} from "../../components/adminDashboard/CourseModernizer/CourseContextEvaluationModal";
import { CourseModernizeProgressModal } from "../../components/adminDashboard/CourseModernizer/CourseModernizeProgressModal";
import type { CourseModernizeProgressStep } from "../../components/adminDashboard/CourseModernizer/CourseModernizeProgressModal";

type RubricBadgeTone = "warning" | "success" | "processing" | "error";

interface UploadedRubricFile {
  fileId: string;
  name: string;
  size: number | null;
}

type ActiveRubricViewModel = NonNullable<ActiveRubricSetQuery["activeRubricSet"]["data"]>;
type RubricSetViewModel = NonNullable<RubricSetQuery["rubricSet"]["data"]>;
type ParsingRubricStatusViewModel = NonNullable<RubricSetParseStatusQuery["rubricSet"]["data"]>;

const rubricInProgressStatuses = [RubricParseStatus.Pending, RubricParseStatus.Parsing];

const getErrorMessage = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong");

const getRubricNameFromFileName = (fileName: string) => fileName.replace(/\.[^/.]+$/, "").trim() || fileName;

function PageHeader({
  rubricBadgePrimaryText,
  rubricBadgeSecondaryText,
  rubricBadgeTone,
  onRubricBadgePress,
}: {
  rubricBadgePrimaryText: string;
  rubricBadgeSecondaryText: string;
  rubricBadgeTone: RubricBadgeTone;
  onRubricBadgePress: () => void;
}) {
  const isConfigured = rubricBadgeTone === "success";
  const badgeIconColor = isConfigured ? "#3E68FE" : rubricBadgeTone === "error" ? "#B42318" : "#B54708";
  const rubricBadgeToneStyle =
    rubricBadgeTone === "success"
      ? styles.configuredRubricBadge
      : rubricBadgeTone === "error"
        ? styles.errorRubricBadge
        : rubricBadgeTone === "processing"
          ? styles.processingRubricBadge
          : styles.warningRubricBadge;
  const rubricPrimaryTextToneStyle =
    rubricBadgeTone === "success"
      ? styles.configuredRubricPrimaryText
      : rubricBadgeTone === "error"
        ? styles.errorRubricText
        : rubricBadgeTone === "processing"
          ? styles.processingRubricText
          : styles.warningRubricText;
  const rubricSecondaryTextToneStyle =
    rubricBadgeTone === "success"
      ? styles.configuredRubricSecondaryText
      : rubricBadgeTone === "error"
        ? styles.errorRubricText
        : rubricBadgeTone === "processing"
          ? styles.processingRubricText
          : styles.warningRubricText;

  return (
    <MotiView from={{ opacity: 0, translateY: 20 }} animate={{ opacity: 1, translateY: 0 }} style={styles.headerCard}>
      <CustomText
        text="Course Modernizer"
        size="m"
        textType="display"
        weight="ultraBold"
        style={styles.headerTitle}
        role="heading"
        aria-level="1"
        useTranslationText={false}
      />
      <CustomSpacing type="vertical" size="s" />
      <Pressable
        role="button"
        accessibilityLabel={`${rubricBadgePrimaryText}. ${rubricBadgeSecondaryText}`}
        onPress={onRubricBadgePress}
        style={[styles.rubricBadge, rubricBadgeToneStyle]}>
        <ShieldIcon color={badgeIconColor} size={14} weight="bold" />
        <CustomText
          text={rubricBadgePrimaryText}
          size="s"
          weight="medium"
          style={[styles.rubricBadgePrimaryText, rubricPrimaryTextToneStyle]}
          useTranslationText={false}
        />
        <CustomText
          text={rubricBadgeSecondaryText}
          size="xs"
          weight="medium"
          style={[styles.rubricBadgeSecondaryText, rubricSecondaryTextToneStyle]}
          useTranslationText={false}
        />
        {isConfigured ? <CaretDownIcon color="#84ADFF" size={12} weight="bold" /> : null}
      </Pressable>
    </MotiView>
  );
}

type ModernizerFilter = "all" | "modernized" | "inProgress" | "notStarted";
type PlaceholderFilter = string;

const REVIEWABLE_MODERNIZATION_STATUSES = new Set<ModernizedAuditStatus>([
  ModernizedAuditStatus.InProgress,
  ModernizedAuditStatus.Modernized,
]);

const ACTIVE_MODERNIZATION_STATUSES = new Set<ModernizedAuditStatus>([
  ModernizedAuditStatus.NotStarted,
  ModernizedAuditStatus.InProgress,
  ModernizedAuditStatus.Modernized,
]);

const getModernizedCourseRank = (status: ModernizedAuditStatus | null | undefined): number => {
  if (!status) {
    return -1;
  }

  switch (status) {
    case ModernizedAuditStatus.InProgress:
      return 3;
    case ModernizedAuditStatus.NotStarted:
      return 2;
    case ModernizedAuditStatus.Modernized:
      return 1;
    default:
      return -1;
  }
};

const getModernizedCourseTimestamp = (created?: string | null, lastModified?: string | null): number => {
  const timestamp = lastModified ?? created;
  return timestamp ? new Date(timestamp).getTime() : 0;
};

const getActiveModernizedCourse = (course: ExternalCourse) => {
  const candidates = (course.modernizedCourses ?? [])
    .filter((modernizedCourse): modernizedCourse is NonNullable<typeof modernizedCourse> => Boolean(modernizedCourse))
    .filter(
      (modernizedCourse) =>
        Boolean(modernizedCourse.status) &&
        ACTIVE_MODERNIZATION_STATUSES.has(modernizedCourse.status as ModernizedAuditStatus)
    )
    .sort((left, right) => {
      const rankDifference = getModernizedCourseRank(right.status) - getModernizedCourseRank(left.status);
      if (rankDifference !== 0) {
        return rankDifference;
      }

      return (
        getModernizedCourseTimestamp(right.created ?? null, right.lastModified ?? null) -
        getModernizedCourseTimestamp(left.created ?? null, left.lastModified ?? null)
      );
    });

  return candidates[0] ?? null;
};

const formatModernizedScore = (totalScore?: number | null, totalMaxScore?: number | null): string => {
  if (typeof totalScore !== "number") {
    return "-";
  }

  if (typeof totalMaxScore === "number" && totalMaxScore > 0) {
    return `${totalScore}/${totalMaxScore}`;
  }

  return `${totalScore}`;
};

const getCourseRowFromExternalCourse = (course: ExternalCourse): CourseModernizerCourseRow => {
  const activeModernizedCourse = getActiveModernizedCourse(course);
  const hasCompletedModernization = (course.modernizedCourses ?? []).some(
    (modernizedCourse) => modernizedCourse?.status === ModernizedAuditStatus.Modernized
  );

  if (!activeModernizedCourse) {
    return {
      course,
      statusLabel: "Not Started",
      scoreLabel: "-",
      canEvaluate: true,
      canViewResults: false,
      canRerun: hasCompletedModernization,
      activeModernizedCourseId: activeModernizedCourse?.id ?? null,
    };
  }

  if (activeModernizedCourse.status === ModernizedAuditStatus.NotStarted) {
    return {
      course,
      statusLabel: "Not Started",
      scoreLabel: "-",
      canEvaluate: false,
      canViewResults: false,
      canRerun: hasCompletedModernization,
      activeModernizedCourseId: activeModernizedCourse.id,
    };
  }

  const isCompleted = activeModernizedCourse.status === ModernizedAuditStatus.Modernized;
  const canViewResults = Boolean(
    activeModernizedCourse.status && REVIEWABLE_MODERNIZATION_STATUSES.has(activeModernizedCourse.status)
  );
  const canRerun =
    (hasCompletedModernization && activeModernizedCourse.status !== ModernizedAuditStatus.InProgress) || canViewResults;

  return {
    course,
    statusLabel: activeModernizedCourse.status === ModernizedAuditStatus.InProgress ? "In Progress" : "Modernized",
    scoreLabel: isCompleted
      ? formatModernizedScore(activeModernizedCourse.totalScore, activeModernizedCourse.totalMaxScore)
      : "-",
    canEvaluate: false,
    canViewResults,
    canRerun,
    activeModernizedCourseId: activeModernizedCourse.id,
  };
};

const toModernizeAudience = (value: CourseContextState["targetAudience"]): ModernizeCourseTargetAudience => {
  switch (value) {
    case "traditionalStudent":
      return ModernizeCourseTargetAudience.TraditionalStudent;
    case "workingProfessional":
      return ModernizeCourseTargetAudience.WorkingProfessional;
    case "graduateStudent":
      return ModernizeCourseTargetAudience.GraduateStudent;
    case "mixedOpen":
    default:
      return ModernizeCourseTargetAudience.Mixed;
  }
};

const toModernizeDeliveryMode = (value: CourseContextState["deliveryMode"]): ModernizedDeliveryMode => {
  switch (value) {
    case "inPerson":
      return ModernizedDeliveryMode.InPerson;
    case "hybrid":
      return ModernizedDeliveryMode.Hybrid;
    case "fullyOnline":
    default:
      return ModernizedDeliveryMode.Online;
  }
};

function CourseModernizerView() {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const isFocused = useIsFocused();
  const { userInfo } = useAuth();
  const showToast = useToast()?.showToast ?? (() => ({ id: "", close: () => {} }));
  const isAdmin = userInfo.userRole !== UserRoleUI.FACULTY;
  const [rubricSheetOpen, setRubricSheetOpen] = useState(false);
  const [selectedRubricFile, setSelectedRubricFile] = useState<UploadedRubricFile | null>(null);
  const [rubricUploadInProgress, setRubricUploadInProgress] = useState(false);
  const [rubricCreateInProgress, setRubricCreateInProgress] = useState(false);
  const [rubricUploadError, setRubricUploadError] = useState<string | null>(null);
  const [parsingRubricId, setParsingRubricId] = useState<string | null>(null);
  const [completedParsingRubricId, setCompletedParsingRubricId] = useState<string | null>(null);
  const [activatingRubricId, setActivatingRubricId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<ModernizerFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [facultyFilter, setFacultyFilter] = useState("all");
  const [facultyFilterDropdownOpen, setFacultyFilterDropdownOpen] = useState(false);
  const [termFilter, setTermFilter] = useState<PlaceholderFilter>("all");
  const [termFilterDropdownOpen, setTermFilterDropdownOpen] = useState(false);
  const [subjectFilter, setSubjectFilter] = useState<PlaceholderFilter>("all");
  const [subjectFilterDropdownOpen, setSubjectFilterDropdownOpen] = useState(false);
  const [programFilter, setProgramFilter] = useState<PlaceholderFilter>("all");
  const [programFilterDropdownOpen, setProgramFilterDropdownOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isCourseContextModalOpen, setIsCourseContextModalOpen] = useState(false);
  const [selectedCourseForEvaluation, setSelectedCourseForEvaluation] = useState<ExternalCourse | null>(null);
  const [courseContext, setCourseContext] = useState<CourseContextState>(DEFAULT_COURSE_CONTEXT);
  const [courseLengthDropdownOpen, setCourseLengthDropdownOpen] = useState(false);
  const [interactionsDropdownOpen, setInteractionsDropdownOpen] = useState(false);
  const [deliveryModeDropdownOpen, setDeliveryModeDropdownOpen] = useState(false);
  const [creditHoursDropdownOpen, setCreditHoursDropdownOpen] = useState(false);
  const [evaluationModalVisible, setEvaluationModalVisible] = useState(false);
  const [activeModernizeJobId, setActiveModernizeJobId] = useState<string | null>(null);
  const [activeExternalCourseId, setActiveExternalCourseId] = useState<string | null>(null);
  const [isStartingEvaluation, setIsStartingEvaluation] = useState(false);
  const [isCancellingEvaluation, setIsCancellingEvaluation] = useState(false);
  const completedCourseRefetchJobIdRef = useRef<string | null>(null);

  const {
    data: activeRubricData,
    loading: activeRubricLoading,
    refetch: refetchActiveRubric,
  } = useActiveRubricSetQuery({});

  const activeRubric = activeRubricData?.activeRubricSet?.data ?? null;
  const {
    data: availableRubricsData,
    loading: availableRubricsLoading,
    refetch: refetchRubricSets,
  } = useRubricSetsBySchoolQuery();
  const availableRubrics = availableRubricsData?.rubricSetsBySchool?.data ?? [];

  const {
    data: parsingRubricData,
    loading: parsingRubricLoading,
    stopPolling: stopParsingRubricPolling,
  } = useRubricSetParseStatusQuery({
    variables: { id: parsingRubricId ?? "" },
    skip: !parsingRubricId,
    pollInterval: parsingRubricId ? 2000 : 0,
  });

  const parsingRubricStatus = parsingRubricData?.rubricSet?.data ?? null;
  const parsingRubricIsReady = parsingRubricStatus?.parseStatus === RubricParseStatus.Ready;
  const {
    data: parsedRubricData,
    loading: parsedRubricLoading,
    refetch: refetchParsedRubric,
  } = useRubricSetQuery({
    variables: { id: parsingRubricId ?? "" },
    skip: !parsingRubricId || !parsingRubricIsReady,
    fetchPolicy: "network-only",
  });
  const parsedRubric = parsedRubricData?.rubricSet?.data ?? null;
  const waitingForReadyRubric = parsingRubricIsReady && parsingRubricId != null && activeRubric?.id !== parsingRubricId;
  const displayedRubric: ActiveRubricViewModel | RubricSetViewModel | ParsingRubricStatusViewModel | null =
    parsedRubric && parsingRubricIsReady
      ? parsedRubric
      : parsingRubricStatus && (!parsingRubricIsReady || waitingForReadyRubric)
        ? parsingRubricStatus
        : activeRubric;
  const [createRubricSetFromFile] = useCreateRubricSetFromFileMutation();
  const [setActiveRubricSet] = useSetActiveRubricSetMutation();
  const [updateRubricSet, { loading: savingRubric }] = useUpdateRubricSetMutation();
  const [modernizeExternalCourse] = useModernizeExternalCourseMutation();
  const [cancelModernizeExternalCourse] = useCancelModernizeExternalCourseMutation();

  const {
    data: modernizeJobStatusData,
    loading: modernizeJobStatusLoading,
    refetch: refetchModernizeJobStatus,
  } = useGetModernizeCourseJobStatusQuery({
    variables: { jobId: activeModernizeJobId ?? "" },
    skip: !activeModernizeJobId,
    pollInterval: activeModernizeJobId ? 2000 : 0,
    fetchPolicy: "network-only",
  });

  const uploadErrorHandler = (message?: string) => {
    const errorMessage = message || "Rubric upload failed";
    setRubricUploadError(errorMessage);
    showToast({ message: errorMessage, type: "error" });
  };

  const { handleFileUpload, allowedMimeTypes, maxFileSize } = useFileUpload({
    storagePath: StoragePathType.Rubrics,
    onError: uploadErrorHandler,
    multiple: false,
  });

  useEffect(() => {
    if (!parsingRubricId || !parsingRubricStatus?.parseStatus) {
      return;
    }

    if (parsingRubricStatus.parseStatus === RubricParseStatus.Ready) {
      stopParsingRubricPolling();
      void Promise.all([refetchActiveRubric(), refetchRubricSets(), refetchParsedRubric()]);
      if (completedParsingRubricId !== parsingRubricId) {
        setCompletedParsingRubricId(parsingRubricId);
        showToast({ message: "Rubric parsed and activated", type: "success" });
      }
      return;
    }

    if (parsingRubricStatus.parseStatus === RubricParseStatus.Failed) {
      stopParsingRubricPolling();
      if (completedParsingRubricId !== parsingRubricId) {
        setCompletedParsingRubricId(parsingRubricId);
        showToast({ message: parsingRubricStatus.parseError || "Rubric parsing failed", type: "error" });
      }
    }
  }, [
    completedParsingRubricId,
    parsingRubricStatus,
    parsingRubricId,
    refetchActiveRubric,
    refetchParsedRubric,
    refetchRubricSets,
    showToast,
    stopParsingRubricPolling,
  ]);

  useEffect(() => {
    if (
      parsingRubricId &&
      activeRubric?.id === parsingRubricId &&
      activeRubric.parseStatus === RubricParseStatus.Ready
    ) {
      setParsingRubricId(null);
    }
  }, [activeRubric, parsingRubricId]);

  useEffect(() => {
    if (!waitingForReadyRubric) {
      return undefined;
    }

    const interval = setInterval(() => {
      void Promise.all([refetchActiveRubric(), refetchRubricSets()]);
    }, 2000);

    return () => clearInterval(interval);
  }, [refetchActiveRubric, refetchRubricSets, waitingForReadyRubric]);

  const handleSelectRubricFile = async () => {
    setRubricUploadError(null);
    setRubricUploadInProgress(true);
    setParsingRubricId(null);
    setCompletedParsingRubricId(null);

    try {
      const processedFiles = await handleFileUpload();
      const processedFile: ProcessedFile | undefined = processedFiles?.[0];

      if (!processedFile) {
        return;
      }

      setSelectedRubricFile({
        fileId: processedFile.file.id,
        name: processedFile.localUploadedFile.name,
        size: processedFile.localUploadedFile.size ?? processedFile.size ?? null,
      });
    } catch (error) {
      const message = getErrorMessage(error);
      setRubricUploadError(message);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    } finally {
      setRubricUploadInProgress(false);
    }
  };

  const handleParseRubric = async () => {
    if (!selectedRubricFile) {
      const message = "Select a rubric file first";
      setRubricUploadError(message);
      showToast({ message, type: "error" });
      return;
    }

    setRubricUploadError(null);
    setRubricCreateInProgress(true);

    try {
      const result = await createRubricSetFromFile({
        variables: {
          input: {
            fileId: selectedRubricFile.fileId,
            name: getRubricNameFromFileName(selectedRubricFile.name),
            setActive: true,
          },
        },
      });

      const response = result.data?.createRubricSetFromFile;
      if (!response?.success || !response.data) {
        throw new Error(response?.message || "Failed to queue rubric parsing");
      }

      setParsingRubricId(response.data.id);
      setSelectedRubricFile(null);
      void refetchRubricSets();
      showToast({ message: "Rubric parsing started", type: "success" });
    } catch (error) {
      const message = getErrorMessage(error);
      setRubricUploadError(message);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    } finally {
      setRubricCreateInProgress(false);
    }
  };

  const handleActivateRubric = async (rubricId: string) => {
    setActivatingRubricId(rubricId);
    try {
      const result = await setActiveRubricSet({
        variables: {
          id: rubricId,
        },
      });
      const response = result.data?.setActiveRubricSet;
      if (!response?.success || !response.data) {
        throw new Error(response?.message || "Failed to activate rubric");
      }

      await Promise.all([refetchActiveRubric(), refetchRubricSets()]);
      showToast({ message: "Rubric activated", type: "success" });
    } catch (error) {
      const message = getErrorMessage(error);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    } finally {
      setActivatingRubricId(null);
    }
  };

  const handleUpdateRubric = async ({
    sections,
    criteria,
  }: {
    sections?: Array<{ id: string; title?: string; description?: string | null; resetToOriginal?: boolean }>;
    criteria?: Array<{
      id: string;
      title?: string;
      description?: string;
      sectionId?: string;
      isDeleted?: boolean;
      resetToOriginal?: boolean;
    }>;
  }) => {
    const editableRubric = displayedRubric?.parseStatus === RubricParseStatus.Ready ? displayedRubric : activeRubric;

    if (!editableRubric) {
      showToast({ message: "No active rubric to update", type: "error" });
      return;
    }

    try {
      const result = await updateRubricSet({
        variables: {
          input: {
            id: editableRubric.id,
            sections,
            criteria,
          },
        },
      });
      const response = result.data?.updateRubricSet;
      if (!response?.success || !response.data) {
        throw new Error(response?.message || "Failed to update rubric");
      }

      await Promise.all([refetchActiveRubric(), refetchRubricSets()]);
      showToast({ message: "Rubric updated", type: "success" });
    } catch (error) {
      const message = getErrorMessage(error);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    }
  };

  const variables = {
    input: {
      page,
      pageSize,
      searchQuery: searchQuery.trim() || undefined,
      facultyId: isAdmin && facultyFilter !== "all" ? facultyFilter : undefined,
    },
  };

  const {
    data,
    loading,
    refetch: refetchExternalCourses,
  } = useListExternalCoursesQuery({
    variables,
  });

  const modernizeStatusPayload = modernizeJobStatusData?.getModernizeCourseJobStatus;
  const generationStatus =
    modernizeStatusPayload?.generationStatus ?? (activeModernizeJobId ? ModernizeCourseGenerationStatus.Running : null);
  const modernizeSteps: CourseModernizeProgressStep[] = (modernizeStatusPayload?.steps ?? []).map((step) => ({
    key: step.key,
    title: step.title,
    agent: step.agent,
    description: step.description,
    status: step.status as CourseModernizeProgressStep["status"],
  }));
  const modernizeCurrentStepIndex = modernizeStatusPayload?.currentStepIndex ?? 0;
  const modernizeTotalSteps = modernizeStatusPayload?.totalSteps ?? modernizeSteps.length;
  const modernizePercent = modernizeStatusPayload?.percent ?? 0;
  const modernizedCourseId = modernizeStatusPayload?.modernizedCourseId ?? null;
  const isGenerationCompleted = generationStatus === ModernizeCourseGenerationStatus.Completed;
  const isGenerationFailed = generationStatus === ModernizeCourseGenerationStatus.Failed;
  const isGenerationTerminal = isGenerationCompleted || isGenerationFailed;
  const normalizedModernizePercent = Math.max(0, Math.min(100, modernizePercent));
  const isEvaluationWorking = Boolean(activeModernizeJobId) && !isGenerationTerminal;

  useEffect(() => {
    if (!evaluationModalVisible || !activeModernizeJobId || isGenerationTerminal) {
      return;
    }

    void refetchModernizeJobStatus();
  }, [activeModernizeJobId, evaluationModalVisible, isGenerationTerminal, refetchModernizeJobStatus]);

  useEffect(() => {
    if (!activeModernizeJobId || !isGenerationCompleted) {
      return;
    }

    if (completedCourseRefetchJobIdRef.current === activeModernizeJobId) {
      return;
    }

    completedCourseRefetchJobIdRef.current = activeModernizeJobId;
    void refetchExternalCourses();
  }, [activeModernizeJobId, isGenerationCompleted, refetchExternalCourses]);

  const handleStartEvaluation = async () => {
    const externalCourseId = selectedCourseForEvaluation?.id;
    if (!externalCourseId || isStartingEvaluation) {
      return;
    }

    setIsStartingEvaluation(true);
    try {
      const result = await modernizeExternalCourse({
        variables: {
          input: {
            externalCourseId,
            context: {
              targetAudience: toModernizeAudience(courseContext.targetAudience),
              lengthInWeeks:
                courseContext.courseLengthWeeks === "selfPaced" ? 16 : Number(courseContext.courseLengthWeeks),
              interactionsPerWeek: courseContext.interactionsPerWeek,
              deliveryMode: toModernizeDeliveryMode(courseContext.deliveryMode),
              creditHours: courseContext.creditHours,
            },
          },
        },
      });
      const payload = result.data?.modernizeExternalCourse;

      if (!payload?.success || !payload.jobId) {
        throw new Error(payload?.message || "Failed to start course evaluation");
      }

      setRubricSheetOpen(false);
      closeCourseContextModal();
      setActiveExternalCourseId(externalCourseId);
      setActiveModernizeJobId(payload.jobId);
      setEvaluationModalVisible(true);
    } catch (error) {
      const message = getErrorMessage(error);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    } finally {
      setIsStartingEvaluation(false);
    }
  };

  const handleCancelEvaluation = async () => {
    if (!activeModernizeJobId || isCancellingEvaluation) {
      return;
    }

    setIsCancellingEvaluation(true);
    try {
      const result = await cancelModernizeExternalCourse({
        variables: {
          jobId: activeModernizeJobId,
        },
      });

      const payload = result.data?.cancelModernizeExternalCourse;

      if (!payload?.success) {
        throw new Error(payload?.message || "Failed to cancel evaluation");
      }

      await refetchModernizeJobStatus();
      showToast({
        message: payload.message || "There was an error during modernization generation. Close and start again.",
        type: "warning",
      });
    } catch (error) {
      const message = getErrorMessage(error);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    } finally {
      setIsCancellingEvaluation(false);
    }
  };

  const handleCloseEvaluationModal = () => {
    if (isCancellingEvaluation) {
      return;
    }

    if (isGenerationTerminal) {
      setEvaluationModalVisible(false);
      setActiveModernizeJobId(null);
      setActiveExternalCourseId(null);
      void refetchExternalCourses();
      return;
    }

    void handleCancelEvaluation();
  };

  const handleContinueToResults = () => {
    if (!isGenerationCompleted) {
      return;
    }

    if (!modernizedCourseId) {
      showToast({ message: "Evaluation finished but no review course id was returned", type: "error" });
      return;
    }

    setEvaluationModalVisible(false);
    setActiveModernizeJobId(null);
    setActiveExternalCourseId(null);
    void refetchExternalCourses();
    navigation.navigate("Course Modernizer Review", { courseId: modernizedCourseId });
  };

  const courses = (data?.listExternalCourses?.data?.courses ?? []).filter((course): course is ExternalCourse =>
    Boolean(course)
  );

  const { data: activeModernizeCourseJobData } = useGetActiveModernizeCourseJobQuery({
    fetchPolicy: "network-only",
  });

  const activeModernizeCourseJob = activeModernizeCourseJobData?.getActiveModernizeCourseJob ?? null;

  useEffect(() => {
    if (!isFocused || activeModernizeJobId || !activeModernizeCourseJob?.jobId) {
      return;
    }

    if (activeModernizeCourseJob.generationStatus !== ModernizeCourseGenerationStatus.Running) {
      return;
    }

    setActiveExternalCourseId(activeModernizeCourseJob.externalCourseId);
    setActiveModernizeJobId(activeModernizeCourseJob.jobId);
    setEvaluationModalVisible(true);
  }, [activeModernizeCourseJob, activeModernizeJobId, isFocused]);

  const courseRows = useMemo(() => courses.map(getCourseRowFromExternalCourse), [courses]);

  const summary = useMemo(() => {
    return courseRows.reduce(
      (counts, row) => {
        counts.totalCourses += 1;

        if (row.statusLabel === "Modernized") {
          counts.modernized += 1;
        } else if (row.statusLabel === "In Progress" || row.statusLabel === "Generating") {
          counts.inProgress += 1;
        } else {
          counts.notStarted += 1;
        }

        return counts;
      },
      { totalCourses: 0, modernized: 0, inProgress: 0, notStarted: 0 }
    );
  }, [courseRows]);

  const visibleBaseCourses = courseRows;

  const facultyOptions = useMemo(() => {
    const optionsMap = new Map<string, string>();
    courses.forEach((course) => {
      course.users?.forEach((faculty) => {
        if (faculty?.id && faculty.name) {
          optionsMap.set(faculty.id, faculty.name);
        }
      });
    });

    return [
      { label: "All Faculty", value: "all" },
      ...Array.from(optionsMap.entries()).map(([value, label]) => ({ label, value })),
    ];
  }, [courses]);

  const visibleCourses = useMemo(() => {
    switch (activeFilter) {
      case "modernized":
        return visibleBaseCourses.filter((course) => course.statusLabel === "Modernized");
      case "inProgress":
        return visibleBaseCourses.filter(
          (course) => course.statusLabel === "In Progress" || course.statusLabel === "Generating"
        );
      case "notStarted":
        return visibleBaseCourses.filter((course) => course.statusLabel === "Not Started");
      case "all":
      default:
        return visibleBaseCourses;
    }
  }, [activeFilter, visibleBaseCourses]);

  const courseWithActiveModernization = useMemo(() => {
    if (activeExternalCourseId) {
      const activeCourse = courses.find((course) => course.id === activeExternalCourseId) ?? null;
      if (activeCourse) {
        return activeCourse;
      }
    }

    const activeJobExternalCourseId = activeModernizeCourseJob?.externalCourseId;
    if (!activeJobExternalCourseId) {
      return null;
    }

    return courses.find((course) => course.id === activeJobExternalCourseId) ?? null;
  }, [activeExternalCourseId, activeModernizeCourseJob?.externalCourseId, courses]);

  const isAnotherCourseModernizing = Boolean(
    courseWithActiveModernization &&
      (activeModernizeCourseJob?.jobId || (activeModernizeJobId && !isGenerationTerminal)) &&
      selectedCourseForEvaluation?.id !== courseWithActiveModernization.id
  );

  const totalItems =
    activeFilter === "all" ? (data?.listExternalCourses?.data?.totalCount ?? 0) : visibleCourses.length;

  const termItems = [{ label: "All Terms", value: "all" }];
  const subjectItems = [{ label: "All Subjects", value: "all" }];
  const programItems = [{ label: "All Programs", value: "all" }];

  const carnegieWorkload = useMemo(() => {
    const effectiveWeeks =
      courseContext.courseLengthWeeks === "selfPaced" ? 16 : Number(courseContext.courseLengthWeeks);
    const directContactHours = courseContext.creditHours * effectiveWeeks;
    const outOfClassHours = directContactHours * 2;
    const totalHours = directContactHours + outOfClassHours;

    return {
      effectiveWeeks,
      directContactHours,
      outOfClassHours,
      totalHours,
    };
  }, [courseContext]);

  const openCourseContextModal = (course: ExternalCourse) => {
    if (courseWithActiveModernization && course.id !== courseWithActiveModernization.id) {
      showToast({
        message: "Another course modernization is already in progress. Please wait until it finishes.",
        type: "warning",
      });
      return;
    }

    setSelectedCourseForEvaluation(course);
    setCourseContext(DEFAULT_COURSE_CONTEXT);
    setCourseLengthDropdownOpen(false);
    setInteractionsDropdownOpen(false);
    setDeliveryModeDropdownOpen(false);
    setCreditHoursDropdownOpen(false);
    setIsCourseContextModalOpen(true);
  };

  const closeCourseContextModal = () => {
    setCourseLengthDropdownOpen(false);
    setInteractionsDropdownOpen(false);
    setDeliveryModeDropdownOpen(false);
    setCreditHoursDropdownOpen(false);
    setIsCourseContextModalOpen(false);
    setSelectedCourseForEvaluation(null);
  };
  const rubricStatus = displayedRubric?.parseStatus;
  const isRubricProcessing = rubricStatus != null && rubricInProgressStatuses.includes(rubricStatus);
  const rubricBadgeTone: RubricBadgeTone =
    rubricStatus === RubricParseStatus.Ready
      ? "success"
      : rubricStatus === RubricParseStatus.Failed
        ? "error"
        : isRubricProcessing
          ? "processing"
          : "warning";
  const rubricBadgePrimaryText =
    rubricStatus === RubricParseStatus.Ready
      ? (displayedRubric?.name ?? "Rubric configured")
      : rubricStatus === RubricParseStatus.Failed
        ? "Rubric needs attention"
        : isRubricProcessing
          ? "Rubric processing"
          : "No rubric configured";
  const rubricBadgeSecondaryText =
    rubricStatus === RubricParseStatus.Ready
      ? (displayedRubric?.detectedFrameworkType ?? "Active")
      : rubricStatus === RubricParseStatus.Failed
        ? "Review"
        : isRubricProcessing
          ? "Working"
          : "Set up";
  const rubricUploadState = rubricCreateInProgress
    ? "creating"
    : rubricUploadInProgress
      ? "uploading"
      : selectedRubricFile
        ? "uploaded"
        : "idle";

  return (
    <AdminLayout withPadding={false} isCourseModernizerPage={true}>
      <View style={styles.pageBackground}>
        <View style={styles.contentContainer}>
          <PageHeader
            rubricBadgePrimaryText={rubricBadgePrimaryText}
            rubricBadgeSecondaryText={rubricBadgeSecondaryText}
            rubricBadgeTone={rubricBadgeTone}
            onRubricBadgePress={() => setRubricSheetOpen(true)}
          />
          <CustomSpacing type="vertical" size="l" />

          <View style={styles.infoCardsContainer}>
            <View style={styles.infoCardWrapper}>
              <CourseInfoCard
                icon={<FileMagnifyingGlassIcon />}
                number={summary.totalCourses}
                title="Total Courses"
                iconColor="rgb(37, 99, 235)"
                activeBorderColor="#7EA6FF"
                isActive={activeFilter === "all"}
                onPress={() => setActiveFilter("all")}
              />
            </View>
            <View style={styles.infoCardWrapper}>
              <CourseInfoCard
                icon={<CheckCircleIcon />}
                number={summary.modernized}
                title="Modernized"
                iconColor="rgb(22, 163, 74)"
                activeBackgroundColor="#F4FDEB"
                activeBorderColor="#C7E9A0"
                isActive={activeFilter === "modernized"}
                onPress={() => setActiveFilter("modernized")}
              />
            </View>
            <View style={styles.infoCardWrapper}>
              <CourseInfoCard
                icon={<ClockIcon />}
                number={summary.inProgress}
                title="In Progress"
                iconColor="rgb(217, 119, 6)"
                activeBackgroundColor="#FFF8E8"
                activeBorderColor="#F4DA9A"
                isActive={activeFilter === "inProgress"}
                onPress={() => setActiveFilter("inProgress")}
              />
            </View>
            <View style={styles.infoCardWrapper}>
              <CourseInfoCard
                icon={<CircleIcon />}
                number={summary.notStarted}
                title="Not Started"
                iconColor="rgb(75, 85, 99)"
                activeBorderColor="#7EA6FF"
                isActive={activeFilter === "notStarted"}
                onPress={() => setActiveFilter("notStarted")}
              />
            </View>
          </View>

          <CustomSpacing type="vertical" size="m" />
          {courseWithActiveModernization ? (
            <>
              <CustomText
                text={`A modernization is currently in progress for ${courseWithActiveModernization.name}. Starting another modernization is disabled until it finishes.`}
                size="s"
                style={styles.activeModernizationNotice}
                useTranslationText={false}
              />
              <CustomSpacing type="vertical" size="m" />
            </>
          ) : null}

          <CourseModernizerCoursesTable
            isAdmin={isAdmin}
            loading={loading}
            visibleCourses={visibleCourses}
            totalItems={totalItems}
            page={page}
            pageSize={pageSize}
            facultyFilter={facultyFilter}
            facultyFilterDropdownOpen={facultyFilterDropdownOpen}
            termFilter={termFilter}
            termFilterDropdownOpen={termFilterDropdownOpen}
            subjectFilter={subjectFilter}
            subjectFilterDropdownOpen={subjectFilterDropdownOpen}
            programFilter={programFilter}
            programFilterDropdownOpen={programFilterDropdownOpen}
            searchQuery={searchQuery}
            facultyOptions={facultyOptions}
            termItems={termItems}
            subjectItems={subjectItems}
            programItems={programItems}
            onFacultyFilterChange={(value) => {
              setFacultyFilter(value);
              setPage(1);
            }}
            setFacultyFilterDropdownOpen={setFacultyFilterDropdownOpen}
            onTermFilterChange={setTermFilter}
            setTermFilterDropdownOpen={setTermFilterDropdownOpen}
            onSubjectFilterChange={setSubjectFilter}
            setSubjectFilterDropdownOpen={setSubjectFilterDropdownOpen}
            onProgramFilterChange={setProgramFilter}
            setProgramFilterDropdownOpen={setProgramFilterDropdownOpen}
            onSearchQueryChange={(value) => {
              setSearchQuery(value);
              setPage(1);
            }}
            onEvaluateCourse={openCourseContextModal}
            onRerunCourse={openCourseContextModal}
            onViewResults={(courseId) => navigation.navigate("Course Modernizer Review", { courseId })}
            onPageChange={setPage}
            onItemsPerPageChange={(value) => {
              setPageSize(value);
              setPage(1);
            }}
          />
        </View>
        <RubricConfigSlideIn
          visible={rubricSheetOpen}
          onClose={() => setRubricSheetOpen(false)}
          rubric={displayedRubric}
          availableRubrics={availableRubrics}
          loadingRubric={
            activeRubricLoading ||
            (parsedRubricLoading && waitingForReadyRubric && parsedRubric == null) ||
            (parsingRubricLoading && displayedRubric == null)
          }
          loadingAvailableRubrics={availableRubricsLoading}
          savingRubric={savingRubric}
          activatingRubricId={activatingRubricId}
          selectedFileName={selectedRubricFile?.name ?? null}
          selectedFileSize={selectedRubricFile?.size ?? null}
          uploadState={rubricUploadState}
          uploadError={rubricUploadError}
          allowedMimeTypes={allowedMimeTypes}
          maxFileSize={maxFileSize}
          onSelectFile={handleSelectRubricFile}
          onClearFile={() => {
            setSelectedRubricFile(null);
            setRubricUploadError(null);
          }}
          onParseRubric={handleParseRubric}
          onUpdateRubric={handleUpdateRubric}
          onActivateRubric={handleActivateRubric}
          showParsedRubricPreview={waitingForReadyRubric && parsedRubric != null}
        />
        <CourseModernizeProgressModal
          visible={evaluationModalVisible}
          onRequestClose={handleCloseEvaluationModal}
          isModernizationTerminal={isGenerationTerminal}
          isEvaluationWorking={isEvaluationWorking}
          isModernizationCompleted={isGenerationCompleted}
          isModernizationFailed={isGenerationFailed}
          statusMessage={modernizeStatusPayload?.message}
          currentStepIndex={modernizeCurrentStepIndex}
          totalSteps={modernizeTotalSteps}
          normalizedPercent={normalizedModernizePercent}
          loading={modernizeJobStatusLoading}
          steps={modernizeSteps}
          isCancellingEvaluation={isCancellingEvaluation}
          onCancelEvaluation={() => void handleCancelEvaluation()}
          onContinueToResults={handleContinueToResults}
        />
      </View>

      <CourseContextEvaluationModal
        visible={isCourseContextModalOpen}
        onClose={closeCourseContextModal}
        onStartEvaluation={handleStartEvaluation}
        startEvaluationDisabled={isAnotherCourseModernizing}
        courseContext={courseContext}
        setCourseContext={setCourseContext}
        carnegieWorkload={carnegieWorkload}
        courseLengthDropdownOpen={courseLengthDropdownOpen}
        setCourseLengthDropdownOpen={setCourseLengthDropdownOpen}
        interactionsDropdownOpen={interactionsDropdownOpen}
        setInteractionsDropdownOpen={setInteractionsDropdownOpen}
        deliveryModeDropdownOpen={deliveryModeDropdownOpen}
        setDeliveryModeDropdownOpen={setDeliveryModeDropdownOpen}
        creditHoursDropdownOpen={creditHoursDropdownOpen}
        setCreditHoursDropdownOpen={setCreditHoursDropdownOpen}
      />
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: "#F2F4F7",
  },
  contentContainer: {
    padding: 24,
    flex: 1,
  },
  infoCardsContainer: {
    flexDirection: "row",
    gap: 16,
    flexWrap: "wrap",
  },
  infoCardWrapper: {
    flex: 1,
    minWidth: 220,
  },
  headerThird: {
    color: "#667085",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  activeModernizationNotice: {
    color: "#B54708",
  },
  headerCard: {
    paddingVertical: 6,
    paddingHorizontal: 0,
    width: "100%",
  },
  headerTitle: {
    color: "#101828",
    fontSize: 24,
    lineHeight: 32,
  },
  rubricBadge: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    borderWidth: 1,
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 12,
    gap: 8,
  },
  warningRubricBadge: {
    backgroundColor: "#FFFAEB",
    borderColor: "#FEDF89",
  },
  configuredRubricBadge: {
    backgroundColor: "#F5F8FF",
    borderColor: "#D1E0FF",
  },
  processingRubricBadge: {
    backgroundColor: "#FFFAEB",
    borderColor: "#FEDF89",
  },
  errorRubricBadge: {
    backgroundColor: "#FEF3F2",
    borderColor: "#FECDCA",
  },
  rubricBadgePrimaryText: {
    lineHeight: 20,
  },
  rubricBadgeSecondaryText: {
    lineHeight: 18,
  },
  warningRubricText: {
    color: "#B54708",
  },
  configuredRubricPrimaryText: {
    color: "#175CD3",
  },
  configuredRubricSecondaryText: {
    color: "#3E68FE",
  },
  processingRubricText: {
    color: "#B54708",
  },
  errorRubricText: {
    color: "#B42318",
  },
});

export default CourseModernizerView;
